import React, { useContext, useEffect } from 'react';

import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import LoginView from './pages/LoginView';
import DashboardView from './pages/DashboardView';
import CommentPage from './pages/CommentPage';

import { UserContext } from './contexts/UserContext';

import ReviewTemplateEditor from './pages/ReviewTemplateEditor/ReviewTemplateEditor';
import ConferenceManagement from './pages/ConferenceManagement';
import ThesisManagement from './pages/ThesisManagement';
import ThesisReviewersManagement from './pages/ThesisReviewersManagement';
import ThesisReviewTaskAssignment from './pages/ThesisReviewTaskAssignment';
import AuthorThesisPage from './pages/AuthorThesisPage';
import CombinedSectionManagement from './pages/CombinedSectionManagement';
import ThesisRatesEditor from './pages/ThesisRatesEditor';
import ThesisElectionPreviewPage from './pages/ThesisElectionPreviewPage';
import FestschriftInfoPage from './pages/FestschriftInfoPage';
import AdminHelpTipsPage from './pages/AdminHelpTipsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import api from './api/axios';

/**
 * Legacy redirect: keep old /conference/:festschrift_id/management
 * and redirect it to the new festschrift-based management route.
 */
function LegacyManagementRedirect() {
  const { festschrift_id } = useParams();
  return <Navigate to={`/management/publishing/${festschrift_id}`} replace />;
}

function getApiBase() {
  const base = api?.defaults?.baseURL || '/api';
  return String(base).replace(/\/$/, '');
}

function ManagementMaterialFileRedirect() {
  const { festschrift_id, material_id } = useParams();

  useEffect(() => {
    const apiBase = getApiBase();
    window.location.replace(
      `${apiBase}/management/publishing/${festschrift_id}/materials/${material_id}/file`,
    );
  }, [festschrift_id, material_id]);

  return <div style={{ padding: 20 }}>{'Downloading…'}</div>;
}

function AuthorMaterialFileRedirect() {
  const { festschrift_id, material_id } = useParams();

  useEffect(() => {
    const apiBase = getApiBase();
    window.location.replace(
      `${apiBase}/author/publishing/${festschrift_id}/materials/${material_id}/file`,
    );
  }, [festschrift_id, material_id]);

  return <div style={{ padding: 20 }}>{'Downloading…'}</div>;
}

const ProtectedRoute = ({ children }) => {
  const { user } = useContext(UserContext);
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { user, isInitialized } = useContext(UserContext);

  if (!isInitialized) {
    return <div>Загрузка...</div>;
  }

  return (
    <Routes>
      {/* AUTH / ROOT */}
      <Route path="/login" element={!user ? <LoginView /> : <Navigate to="/dashboard" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* DASHBOARD */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardView />
          </ProtectedRoute>
        }
      />

      {/* REVIEW PAGE (article-based, review_id as before) */}
      <Route
        path="/publishing/:festschrift_id/review/:review_id"
        element={
          <ProtectedRoute>
            <CommentPage />
          </ProtectedRoute>
        }
      />

      {/* FESTSCHRIFT MANAGEMENT ROOT */}
      <Route
        path="/management/publishing/:festschrift_id"
        element={
          <ProtectedRoute>
            <ConferenceManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/management/publishing/:festschrift_id/materials/:material_id/file"
        element={
          <ProtectedRoute>
            <ManagementMaterialFileRedirect />
          </ProtectedRoute>
        }
      />

      <Route
        path="/author/publishing/:festschrift_id/materials/:material_id/file"
        element={
          <ProtectedRoute>
            <AuthorMaterialFileRedirect />
          </ProtectedRoute>
        }
      />

      {/* SECTION + ARTICLES MANAGEMENT (list of articles in a section) */}
      <Route
        path="/management/publishing/:festschrift_id/sections/:section_id/articles"
        element={
          <ProtectedRoute>
            <CombinedSectionManagement />
          </ProtectedRoute>
        }
      />

      {/* SINGLE ARTICLE MANAGEMENT */}
      <Route
        path="/management/publishing/:festschrift_id/sections/:section_id/articles/:article_id"
        element={
          <ProtectedRoute>
            <ThesisManagement />
          </ProtectedRoute>
        }
      />

      {/* ARTICLE REVIEWERS MANAGEMENT */}
      <Route
        path="/management/publishing/:festschrift_id/sections/:section_id/articles/:article_id/reviewers"
        element={
          <ProtectedRoute>
            <ThesisReviewersManagement />
          </ProtectedRoute>
        }
      />

      {/* LEGACY ENTRYPOINT: /conference/:festschrift_id/management → new management route */}
      <Route path="/conference/:festschrift_id/management" element={<LegacyManagementRedirect />} />

      {/* REVIEW TEMPLATE EDITOR (festschrift-based) */}
      <Route
        path="/publishing/:festschrift_id/template"
        element={
          <ProtectedRoute>
            <ReviewTemplateEditor />
          </ProtectedRoute>
        }
      />

      {/* REVIEW TASK ASSIGNMENT FOR AN ARTICLE */}
      <Route
        path="/publishing/:festschrift_id/sections/:section_id/articles/:article_id/task"
        element={
          <ProtectedRoute>
            <ThesisReviewTaskAssignment />
          </ProtectedRoute>
        }
      />

      {/* AUTHOR VIEW OF THEIR ARTICLE (was /author/thesis/:thesis_id) */}
      <Route
        path="/author/articles/:article_id"
        element={
          <ProtectedRoute>
            <AuthorThesisPage />
          </ProtectedRoute>
        }
      />
      <Route path="/publishing/:festschrift_id/info" element={<FestschriftInfoPage />} />

      {/* ELECTIONS (ratings) FOR SECTION ARTICLES */}
      <Route
        path="/publishing/:festschrift_id/sections/:festschrift_section_id/election"
        element={
          <ProtectedRoute>
            <ThesisRatesEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publishing/:festschrift_id/sections/:section_id/election/:article_id"
        element={
          <ProtectedRoute>
            <ThesisElectionPreviewPage />
          </ProtectedRoute>
        }
      />
      {/* ADMIN HELP TIPS MANAGEMENT */}
      <Route
        path="/admin/help-tips"
        element={
          <ProtectedRoute>
            <AdminHelpTipsPage />
          </ProtectedRoute>
        }
      />
      {/* USER SETTINGS */}
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
